/**
 * conflictBand — should this list show its conflict band (and history icon)?
 *
 * WHY (Walter 2026-09-13). He resolved a conflict and the band came straight
 * back; when he clicked it, it vanished. Nothing new had happened — history's
 * newest entry was his own "(kept on resolve)". "Mark resolved" clears
 * `conflictPendingReview` in one atomic write and only then closes the dialog,
 * but the panel learns about that write through its normal snapshot, which
 * can trail by seconds. For those seconds it kept drawing the OLD flag.
 *
 * So the panel remembers which flag it just resolved and stops drawing that
 * exact flag straight away. It does not hide the band in general: every writer
 * of `conflictPendingReview` stamps it with a fresh `serverTimestamp()`
 * (conflictResolver, historyConflict, the /api/saveList fallback), so a
 * GENUINELY new conflict arrives with a different value and still shows.
 */

/**
 * flagKey(flag) → a comparable string for a conflictPendingReview value, or
 * null when there is no pending conflict.
 *
 * Shape-agnostic on purpose: the same instant can reach the panel as an SDK
 * Timestamp (live snapshot), a revived `{ seconds, nanoseconds }` (the REST
 * read fallback), a Date, or a number. All normalise to milliseconds, so one
 * moment always yields one key.
 */
export function flagKey(flag) {
  if (flag == null || flag === false) return null;
  if (typeof flag.toMillis === 'function') {
    const ms = flag.toMillis();
    return Number.isFinite(ms) ? `ms:${ms}` : null;
  }
  if (flag instanceof Date) {
    const ms = flag.getTime();
    return Number.isFinite(ms) ? `ms:${ms}` : null;
  }
  if (typeof flag === 'number') return Number.isFinite(flag) ? `ms:${flag}` : null;
  if (typeof flag === 'object' && typeof flag.seconds === 'number') {
    const ms = flag.seconds * 1000 + Math.floor((flag.nanoseconds || 0) / 1e6);
    return `ms:${ms}`;
  }
  // A legacy truthy flag with no instant attached. It can't be told apart from
  // a later one, but nothing writes this shape any more.
  return 'truthy';
}

/**
 * isConflictBandVisible({ flag, dismissedKey, hasMarker })
 *
 *   flag         — list.conflictPendingReview as the panel currently sees it
 *   dismissedKey — flagKey of the flag this panel last resolved, or null
 *   hasMarker    — the pre-2026-05-17 in-content conflict marker is present
 *
 * The legacy marker always shows: resolving strips it from the content, so it
 * can't be stale in the way the flag can.
 */
export function isConflictBandVisible({ flag, dismissedKey = null, hasMarker = false }) {
  if (hasMarker) return true;
  const key = flagKey(flag);
  if (!key) return false;
  return key !== dismissedKey;
}
